import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject, forkJoin, EMPTY } from 'rxjs';
import {
  AuthenticateUserState,
  UpdateTokens,
  RemoveAuthenticateUser,
} from './core/store/authenticate-user.state';
import { Store } from '@ngxs/store';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import {
  catchError,
  filter,
  map,
  mergeMap,
  share,
  take,
  takeUntil,
} from 'rxjs/operators';
import { PageTitleService } from './core/services/page-title.service';
import { HeirarchyFilterServiceService } from './shared/service/heirarchy-filter-service/heirarchy-filter-service.service';
import { ProfileStoreService } from './core/services/profile-store.service';
import { LoaderService } from './shared/service/loader/loader.service';
import { NetworkStatusService } from './shared/service/base-services/network-status.service';
import { CustomToastrService } from './shared/ngx-toastr/custom-toastr.service';
import { SessionExpiredService } from './shared/service/session-expired/session-expired.service';
import { FontRegisterService } from './shared/service/font-register.service';
import {
  OidcSecurityService,
  PublicEventsService,
  EventTypes,
} from 'angular-auth-oidc-client';
import { TimeUtil } from './shared/utils/time-util';
import { UserActivityTimeLocalStore } from './core/localStore/user-activity-time';
import {
  AUTHRETURN_ROUTE,
  FORBIDDEN_ROUTE,
  LOGIN_ROUTE,
  UNAUTHORIZED_ROUTE,
} from './shared/constants';
import { TokenRenewalCoordinatorService } from './core/services/token-renewal-coordinator.service';
import { ReturnUrlStoreService } from './core/services/return-url-store.service';
import { SessionWarningService } from './core/services/session-warning.service';
import { JobProgressService } from './core/services/job-progress.service';
import { buildFreshLoginAuthOptions } from './core/utils/authorize-options';

const UserInactiveTimeoutMs = 30 * 60 * 1000;
const DoWorkFrequencyMs = 60 * 1000;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'SYRC';
  isShrunk = false;
  defaultLoad = true;
  isAuthenticated = false;
  public showFullLayout = true;
  pageTitle = '';
  isAuthenticated$: Observable<boolean | null | undefined> | undefined;
  isLoading$ = this.loaderService.loading$;
  sessionExpired = false;
  /** The session-expired dialog is the informational "sign-in is in progress in another tab" form. */
  signInInProgressElsewhere = false;
  // SYRC-6339: proactive pre-expiry warning state.
  sessionWarningVisible = false;
  sessionWarningSecondsRemaining = 0;
  private hasShownOfflineMessage = false;
  private destroy$ = new Subject<void>();
  private lastActivityTime = Date.now();
  private doWorkIntervalId: any;
  /** One-shot latch so reconcileAuthState() and the SilentRenewFailed handler
   *  cannot both fire authorize() and race the STS into a blank page. */
  private reauthInProgress = false;

  @HostListener('document:visibilitychange', [])
  onVisibilityChange() {
    this.handleVisibilityChange();
  }

  @HostListener('document:mousemove', [])
  @HostListener('document:keydown', [])
  @HostListener('document:click', [])
  @HostListener('document:scroll', [])
  @HostListener('document:touchstart', [])
  onUserActivity() {
    this.handleActivity();
  }

  constructor(
    private store: Store,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private pageTitleService: PageTitleService,
    private _heirarchyFilterService: HeirarchyFilterServiceService,
    private profile: ProfileStoreService,
    private loaderService: LoaderService,
    public networkService: NetworkStatusService,
    private notificationService: CustomToastrService,
    private fontService: FontRegisterService,
    private sessionExpiredService: SessionExpiredService,
    private oidcSecurityService: OidcSecurityService,
    private publicEventsService: PublicEventsService,
    private tokenRenewalCoordinator: TokenRenewalCoordinatorService,
    private returnUrlStore: ReturnUrlStoreService,
    private sessionWarningService: SessionWarningService,
    private jobProgress: JobProgressService,
  ) {
    this.isAuthenticated$ = this.store.select(
      AuthenticateUserState.getIsAuthenticated,
    );
    this.fontService.registerFonts();
  }

  ngOnInit(): void {
    this.handleNetworkStatus();
    this.handleRouteChanges();
    this.handlePageTitle();

    //Fetch the isAutheticated from store
    this.isAuthenticated = this.store.selectSnapshot(
      AuthenticateUserState.getIsAuthenticated,
    );

    if (this.isAuthenticated) {
      this.defaultLoad = false;
      // Ensure profile menu data is populated if roles are missing
      const corViewUserId = this.store.selectSnapshot(
        AuthenticateUserState.getLoggedInCorViewUserId,
      );
      if (corViewUserId) {
        // On a failed request the store keeps its roles and RoutePermissionService reports it.
        this.profile
          .ensureProfileMenuData(corViewUserId)
          .catch((error) =>
            console.warn('Profile refresh on start failed:', error),
          );
      }
      //Used to get data for filter when page is refreshed.
      this.loadData();
      // SYRC-7180: open the shared per-user job-progress subscription and resume
      // any background jobs (e.g. PC deletion) that were active before this
      // (re)load, so their toasts reappear and continue until done.
      this.jobProgress.init();
    }

    this.doWorkIntervalId = setInterval(
      this.doWorkRegardingUserActivityAndInactivity,
      DoWorkFrequencyMs,
    );

    // Confirm the session and start the renewal coordinator when authenticated
    // (see reconcileAuthState). silentRenew is off, so the coordinator owns
    // renewal. Skip on /auth-return: AuthReturnComponent already calls checkAuth()
    // there to redeem the single-use code, and a second call would race it.
    if (!this.isOnAuthCallbackRoute()) {
      this.oidcSecurityService
        .checkAuth()
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe(({ isAuthenticated }) =>
          this.reconcileAuthState(isAuthenticated),
        );
    }

    // Pick up tokens written to localStorage by a renewal or another tab.
    this.syncTokensToStore();

    // One shared subscription to the library's events; handlers branch off it.
    const events$ = this.publicEventsService
      .registerForEvents()
      .pipe(takeUntil(this.destroy$), share());

    // A renewal writes fresh tokens and fires NewAuthenticationResult; sync them
    // into NGXS immediately so token consumers (e.g. base-aws-request.service)
    // don't wait for the periodic doWork poll.
    events$
      .pipe(
        filter((event) => event.type === EventTypes.NewAuthenticationResult),
      )
      .subscribe(() => this.syncTokensToStore());

    // A renewal failure means the session is dead (refresh token expired/rotated)
    // and reconcileAuthState — which only runs at startup — won't catch it. Bounce
    // through the STS, sharing reauthenticate()'s one-shot latch so it can't
    // double-redirect alongside the startup reconcile.
    events$
      .pipe(filter((event) => event.type === EventTypes.SilentRenewFailed))
      .subscribe(() =>
        // SYRC-7399 (AC 4): a rejected refresh must not be quietly re-established by a silent
        // bounce, so force a fresh login. See buildFreshLoginAuthOptions().
        this.reauthenticate('silent renew failed', true),
      );

    // A refresh-token failure (missing or rejected token) wipes the session without raising
    // SilentRenewFailed, so the coordinator reports an unrecoverable renewal itself. Same
    // one-shot latch as above, so the two signals cannot double-redirect. The coordinator also
    // reports a token that disappeared (this tab never failed itself). Before taking the
    // cross-tab claim it asks whether this tab can act at all, so a tab that would ignore the
    // signal (logged out, on a logged-out page, already re-authenticating) never holds the claim
    // that another tab needs.
    //
    // SYRC-7399 (AC 4): this is the path a failed renewal actually takes at runtime (the library
    // never raises SilentRenewFailed with silentRenew off), so the fresh login is forced here (in
    // Auth0 mode only — see reauthenticate()). Every emission means the session is unrecoverable AND
    // this tab won the cross-tab claim to sign the user in (other tabs report sessionLostOnReturn$ /
    // signInElsewhere$ instead and never get here).
    // A rejected refresh cannot be told apart from a transient server error at this layer: the
    // library wipes the tokens on ANY non-network refresh failure and replaces the HTTP error with a
    // generic one, and the coordinator also gives up after repeated failures with a stored, expired
    // token. All of these end the session, so they are treated alike: the user signs in again
    // instead of being silently bounced back into a session that may have been revoked.
    this.tokenRenewalCoordinator.setReauthGuard(() => this.canReauthenticate());
    this.tokenRenewalCoordinator.renewalFailed$
      .pipe(takeUntil(this.destroy$))
      .subscribe((reason) => {
        if (this.canReauthenticate()) {
          this.reauthenticate(reason, true);
        }
      });

    // The user is looking at a tab with no valid session while another tab is already signing
    // them in. A second sign-in from here would overwrite the shared PKCE verifier and nonce and
    // break the first, so only say so. The dialog goes away by itself once the session is back.
    this.tokenRenewalCoordinator.signInElsewhere$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.showSignInInProgressPrompt());
    // The sign-in the other tab started never finished: drop the informational form and show the
    // normal session-expired prompt, whose OK signs the user in from this tab.
    this.tokenRenewalCoordinator.signInAbandoned$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.showSessionExpiredPrompt());
    // The user came back to this tab after the session died and nobody is signing them in: show
    // the normal session-expired prompt rather than redirecting on return.
    this.tokenRenewalCoordinator.sessionLostOnReturn$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.showSessionExpiredPrompt());
    this.tokenRenewalCoordinator.sessionRestored$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onSessionRestored());

    // SYRC-6339: render the proactive expiry warning off the service's state.
    this.sessionWarningService.warning$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.sessionWarningVisible = state.show;
        this.sessionWarningSecondsRemaining = state.secondsRemaining;
      });

    // Listen for logout events from other tabs
    window.addEventListener('storage', this.handleStorageEvent);

    this.lastActivityTime = Date.now();
    UserActivityTimeLocalStore.saveLastUserActivityTime(this.lastActivityTime);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('storage', this.handleStorageEvent);

    if (this.doWorkIntervalId) {
      clearInterval(this.doWorkIntervalId);
    }

    this.tokenRenewalCoordinator.setReauthGuard(null);
    this.tokenRenewalCoordinator.stop();
    this.sessionWarningService.stop();
  }

  /**
   * True when the current page already owns its own checkAuth() call — the OIDC redirect
   * callback (/auth-return) or the /login landing page (LoginComponent self-hydrates). A second,
   * concurrent call from here would race it for the same refresh-token redemption (SYRC-6101
   * review). We read window.location directly rather than Router.url because, during app
   * bootstrap, the router may not have resolved the initial navigation yet — whereas
   * window.location already reflects the STS redirect.
   */
  private isOnAuthCallbackRoute(): boolean {
    const path = window.location.pathname;
    return path.includes(AUTHRETURN_ROUTE) || path.includes(LOGIN_ROUTE);
  }

  /**
   * True when the current route already represents a logged-out / denied state,
   * so we must not bounce the user back through the STS (avoids redirect loops).
   */
  private isOnLoggedOutRoute(): boolean {
    const path = window.location.pathname;
    return (
      path.includes(UNAUTHORIZED_ROUTE) ||
      path.includes(FORBIDDEN_ROUTE) ||
      // The branded login page is itself a logged-out route (SYRC-6097): bouncing
      // its visitors through the STS would defeat the page and can loop.
      path.includes(LOGIN_ROUTE)
    );
  }

  /**
   * Reconciles the OIDC library's verdict with the persisted NGXS auth flag.
   *
   * The flag is rehydrated from localStorage on reload and can outlive the real
   * session — e.g. returning after the idToken has expired. The AuthGuard admits
   * the user on that stale flag (it short-circuits on selectSnapshot before ever
   * calling checkAuth), so without this the app would silently trust a dead
   * session. When the library reports not-authenticated but the store still
   * claims we are, the flag is stale: clear it and re-authenticate — mirroring
   * the AuthGuard's own checkAuth()===false → authorize() path. If the STS
   * session is still alive this is a seamless silent bounce; otherwise the user
   * lands on the login page.
   *
   * Renewal is owned by TokenRenewalCoordinatorService (started below), not
   * forceRefreshSession() here.
   */
  private reconcileAuthState(isAuthenticated: boolean): void {
    if (isAuthenticated) {
      // Session valid — start renewal. start() is idempotent.
      this.tokenRenewalCoordinator.start();
      // SYRC-6339: start watching for a near-expiry warning too (idempotent).
      this.sessionWarningService.start();
      return;
    }

    const storeClaimsAuthenticated = this.store.selectSnapshot(
      AuthenticateUserState.getIsAuthenticated,
    );
    if (!storeClaimsAuthenticated || this.isOnLoggedOutRoute()) {
      // App and library agree the user is logged out (the AuthGuard handles
      // redirects), or we're already on a logged-out route — nothing to do.
      return;
    }

    // Divergence: the persisted flag is stale. Clear it and re-authenticate.
    this.reauthenticate(
      'persisted session is no longer valid (checkAuth=false)',
    );
  }

  /**
   * Whether this tab is in a position to re-authenticate a dead session: the store still claims
   * a session (it is cleared by an explicit logout), no re-authentication is already under way,
   * and the page is not a logged-out / denied route.
   */
  private canReauthenticate(): boolean {
    return (
      !this.reauthInProgress &&
      !this.isOnLoggedOutRoute() &&
      !!this.store.selectSnapshot(AuthenticateUserState.getIsAuthenticated)
    );
  }

  /**
   * Single entry point for re-authenticating a stale/expired session.
   *
   * Both reconcileAuthState() (once, at startup) and the SilentRenewFailed
   * handler (on a mid-session renew failure) can detect a dead session at
   * nearly the same moment. Two concurrent authorize() calls generate two
   * different state/nonce pairs and make the STS render a blank page, so we
   * guard with a one-shot latch: the first caller wins and triggers the
   * redirect; any competing caller is a no-op. authorize() navigates the whole
   * page away, so the latch only needs to hold for the current page lifetime.
   *
   * MFA / re-challenge policy (SYRC-7399, AC 3 and AC 4):
   *   - Stale persisted state at startup (forceFreshLogin=false): a plain bounce. If the IdP
   *     session is still alive Auth0 re-applies its per-user MFA decision — an MFA user is
   *     challenged again, a non-MFA user returns silently (verified on QA). This is the normal
   *     "came back after the token expired" case, so it stays seamless.
   *   - A failed renewal (forceFreshLogin=true): force `prompt=login`, so a session that can no
   *     longer be renewed is never quietly re-established — the user re-authenticates and Auth0
   *     re-applies MFA for users who have it. Auth0 mode only: under the legacy CorView STS the
   *     bounce stays a plain authorize(), exactly as before SYRC-7399 (profile.isAuth0Enabled is
   *     the same provider flag this component already uses to choose /login vs /unauthorized; until
   *     it resolves it is false, which safely falls back to the plain bounce).
   */
  private reauthenticate(reason: string, forceFreshLogin = false): void {
    if (this.reauthInProgress || this.isOnLoggedOutRoute()) {
      return;
    }
    this.reauthInProgress = true;
    console.warn(
      `[AUTH] Session no longer valid (${reason}) — clearing stale state and re-authenticating.`,
    );
    // Persist the requested deep link before bouncing through the STS. This branch runs when the
    // AuthGuard's fast path admitted the user on a stale NGXS flag and so never reached the guard's
    // own save (SYRC-7252). There is no RouterStateSnapshot here, so read the URL from
    // window.location — the router may not have resolved the initial navigation yet. The one-shot
    // latch above guarantees this save runs at most once per page lifetime.
    //
    // Wrapped defensively: persisting the deep link is best-effort and must never be able to abort
    // the re-authentication that recovers the session (AC 5). ReturnUrlStoreService already guards
    // storage access, but this keeps recovery correct even if that ever changes.
    try {
      this.returnUrlStore.save(
        window.location.pathname + window.location.search,
      );
    } catch (err) {
      console.warn(
        '[AUTH] Failed to persist deep link before re-authentication:',
        err,
      );
    }
    this.store.dispatch(new RemoveAuthenticateUser());
    this.isAuthenticated = false;
    // Every path in here (startup check, SilentRenewFailed, coordinator) marks the sign-in, so
    // other tabs never offer a logout that would break it.
    this.tokenRenewalCoordinator.markSignInStarted();
    if (forceFreshLogin && this.profile.isAuth0Enabled) {
      this.oidcSecurityService.authorize(
        undefined,
        buildFreshLoginAuthOptions(),
      );
    } else {
      this.oidcSecurityService.authorize();
    }
  }

  private handleVisibilityChange = () => {
    // When tab becomes visible again, sync tokens from localStorage
    // (another tab may have refreshed them via silent renew)
    if (document.visibilityState === 'visible') {
      this.syncTokensToStore();
      // Tell a returning user right away if the session died while they were elsewhere.
      void this.tokenRenewalCoordinator.checkNow({ returning: true });
    }
    this.doWorkRegardingUserActivityAndInactivity();
  };

  /**
   * Handles localStorage changes from other tabs.
   * When another tab logs out, it writes a 'logout-event' key which triggers
   * this handler in all other open tabs to redirect to the login page.
   * We do NOT call logoffLocal() here because that would clear the shared
   * localStorage tokens — if the user logs back in on one tab, the other
   * tabs need to be able to pick up those tokens on refresh.
   */
  private handleStorageEvent = (event: StorageEvent) => {
    if (event.key === 'logout-event' && event.newValue) {
      console.log('[CROSS-TAB] Detected logout in another tab');
      this.store.dispatch(new RemoveAuthenticateUser());
      this.isAuthenticated = false;
      this.router.navigate([
        this.profile.isAuth0Enabled ? '/login' : '/unauthorized',
      ]);
    }
  };

  private handleActivity = () => {
    this.lastActivityTime = Date.now();
  };

  private doWorkRegardingUserActivityAndInactivity = () => {
    const lastActivityTimeFromLocalStore =
      UserActivityTimeLocalStore.readLastUserActivityTime();

    if (this.lastActivityTime <= lastActivityTimeFromLocalStore) {
      this.lastActivityTime = lastActivityTimeFromLocalStore;
    } else {
      UserActivityTimeLocalStore.saveLastUserActivityTime(
        this.lastActivityTime,
      );
    }

    const now = Date.now();
    const inactiveDuration = now - this.lastActivityTime;

    if (inactiveDuration > UserInactiveTimeoutMs) {
      console.warn(
        '[INACTIVITY] ' +
          new Date(now).toLocaleTimeString() +
          ' - User inactive for ' +
          TimeUtil.convertMillisecondsToMinutes(UserInactiveTimeoutMs).toFixed(
            2,
          ) +
          ' minutes, force to log out',
      );
      // logoutUser() alone doesn't navigate — without this the user is left on a dead
      // authenticated screen after an inactivity-triggered logout. Navigate only once it has
      // finished: /login opened while the tokens are still live re-bootstraps the session.
      void this.profile.logoutUser().then(() => {
        if (this.profile.isAuth0Enabled) {
          this.router.navigate(['/login']);
        }
      });
    } else {
      console.log(
        '[ACTIVITY] ' +
          new Date(now).toLocaleTimeString() +
          ' - Last user activity occurred ' +
          TimeUtil.convertMillisecondsToMinutes(inactiveDuration).toFixed(2) +
          ' minute(s) ago' +
          '(' +
          this.lastActivityTime +
          ')',
      );
    }

    // Sync tokens from localStorage into NGXS store (handles cross-tab renewal)
    this.syncTokensToStore();
  };

  /**
   * Reads current tokens from the OIDC library (backed by localStorage)
   * and updates the NGXS store so other parts of the app have fresh tokens.
   * This handles the cross-tab scenario: another tab's silent renew writes
   * new tokens to localStorage, and this tab picks them up here.
   */
  private syncTokensToStore(): void {
    forkJoin({
      idToken: this.oidcSecurityService.getIdToken().pipe(take(1)),
      accessToken: this.oidcSecurityService.getAccessToken().pipe(take(1)),
    })
      .pipe(
        catchError((error) => {
          console.warn('Failed to sync tokens to store:', error);
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(({ idToken, accessToken }) => {
        if (idToken && accessToken) {
          this.store.dispatch(new UpdateTokens(idToken, accessToken));
        }
      });
  }

  private async loadData() {
    if (this.isAuthenticated) {
      await this._heirarchyFilterService.initialize();
    }
  }

  private handleNetworkStatus(): void {
    this.networkService.isOnline$
      .pipe(takeUntil(this.destroy$))
      .subscribe((online) => {
        if (!online && !this.hasShownOfflineMessage) {
          this.notificationService.showError('Network is offline.');
          this.hasShownOfflineMessage = true;
        }

        if (online && this.hasShownOfflineMessage) {
          this.notificationService.showSuccess('You are back online.');
          this.hasShownOfflineMessage = false;
        }
      });
  }

  private handleRouteChanges(): void {
    // Routes that render without the app chrome (nav / sidebar / breadcrumb).
    const excludedRoutes = [`/${AUTHRETURN_ROUTE}`, `/${LOGIN_ROUTE}`];
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        this.showFullLayout = !excludedRoutes.some((path) =>
          event.urlAfterRedirects.includes(path),
        );
      });
  }

  private handlePageTitle(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap((route) => route.data),
        takeUntil(this.destroy$),
      )
      .subscribe((data) => {
        const title = data['title'];
        this.pageTitleService.setPageTitle(title);
      });
  }

  handleSessionExpired() {
    // While another tab is signing in, only ever show the informational form: the ordinary
    // form's OK logs out and would break that sign-in. This also covers a 401 that arrives after
    // the user dismissed the informational form.
    if (
      this.signInInProgressElsewhere ||
      this.tokenRenewalCoordinator.isSignInInProgressElsewhere()
    ) {
      this.showSignInInProgressPrompt();
      return;
    }
    this.sessionExpired = true;
  }

  private showSessionExpiredPrompt(): void {
    this.signInInProgressElsewhere = false;
    this.sessionExpired = true;
  }

  private showSignInInProgressPrompt(): void {
    // Also switches an ordinary session-expired dialog that is already up (e.g. raised by a 401
    // from an API call): its OK logs out, which would break the sign-in in progress.
    this.signInInProgressElsewhere = true;
    this.sessionExpired = true;
  }

  private dismissSignInInProgressPrompt(): void {
    if (!this.signInInProgressElsewhere) {
      return;
    }
    this.closeSessionExpiredDialog();
  }

  /**
   * A valid session is back (sign-in finished in another tab). Close either form of the dialog:
   * OK on the ordinary form would log out the session that was just restored.
   */
  private onSessionRestored(): void {
    if (!this.sessionExpired) {
      return;
    }
    this.closeSessionExpiredDialog();
  }

  private closeSessionExpiredDialog(): void {
    this.signInInProgressElsewhere = false;
    this.sessionExpired = false;
    this.sessionExpiredService.resetDialogFlag();
  }

  /**
   * SYRC-6339: user chose to keep working — renew the token now via the same
   * coordinator path and clear the warning. If renewal fails and leaves the
   * session unrecoverable, the coordinator's renewalFailed$ signal (or the
   * library's SilentRenewFailed) still routes through reauthenticate().
   */
  onSessionStayLoggedIn() {
    this.sessionWarningService.acknowledge();
    this.tokenRenewalCoordinator.renewNow().catch((error) => {
      console.warn(
        '[TOKEN-RENEW] Renewal from the expiry warning failed:',
        error,
      );
    });
  }

  /** SYRC-6339: user dismissed the warning without renewing. */
  onSessionWarningDismiss() {
    this.sessionWarningService.acknowledge();
  }

  async onDialogClose() {
    if (this.signInInProgressElsewhere) {
      // Only dismiss: logging out here would revoke the tokens and clear the shared sign-in data
      // that the other tab needs to finish.
      this.dismissSignInInProgressPrompt();
      return;
    }
    this.sessionExpired = false;
    this.sessionExpiredService.resetDialogFlag();
    await this.profile.logoutUser(); // clear session
    this.router.navigate([this.profile.isAuth0Enabled ? '/login' : '/']);
  }

  toggleShrink(isShrunk: boolean) {
    this.isShrunk = isShrunk;
  }
}
